import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error caught by ErrorBoundary:', error, errorInfo);
    
    // Check if error is a chunk loading error (stale build cache / expected expression got '<')
    const errorMsg = error?.message || '';
    const isChunkError = 
      errorMsg.includes("expected expression") ||
      errorMsg.includes("got '<'") ||
      errorMsg.includes("Failed to fetch dynamically imported module") ||
      errorMsg.includes("Importing a module script failed");

    if (isChunkError) {
      const chunkReloadKey = 'chunk_reload_attempts';
      const attempts = parseInt(sessionStorage.getItem(chunkReloadKey) || '0', 10);
      if (attempts < 2) {
        sessionStorage.setItem(chunkReloadKey, String(attempts + 1));
        window.location.reload();
      }
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/anasayfa';
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center p-6 text-center">
          <div className="bg-surface border border-border p-8 rounded-3xl max-w-md w-full shadow-xl animate-in fade-in zoom-in-95 duration-200">
            <div className="w-16 h-16 bg-danger-100 dark:bg-danger-500/10 text-danger-500 rounded-full flex items-center justify-center mx-auto mb-5 shadow-sm border border-danger-500/20">
              <AlertTriangle size={32} />
            </div>

            <h2 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight mb-2">
              Bir Hata Oluştu
            </h2>

            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed mb-6">
              Bu sayfa yüklenirken beklenmeyen bir sorun meydana geldi. Sayfayı yenileyerek veya ana sayfaya dönerek devam edebilirsiniz.
            </p>

            {this.state.error?.message && (
              <div className="mb-6 p-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-left">
                <p className="text-[11px] font-mono text-slate-600 dark:text-slate-400 truncate">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={this.handleReload}
                className="flex-1 py-3 px-4 bg-accent-500 hover:bg-accent-600 text-white font-bold text-xs rounded-xl transition-all shadow-md shadow-accent-500/20 active:scale-95 flex items-center justify-center gap-2"
              >
                <RefreshCw size={14} /> Sayfayı Yenile
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex-1 py-3 px-4 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold text-xs rounded-xl transition-all active:scale-95 flex items-center justify-center gap-2"
              >
                <Home size={14} /> Ana Sayfa
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
